import React, { useEffect, useState, useMemo, type ReactNode, type ChangeEvent } from "react";
import { useNavigate } from "react-router-dom";
import { 
  CheckCircle, ChevronRight, FileText, User, ArrowRight, 
  Briefcase, CreditCard, Phone, Shield, Building2, Award, 
  BookOpen, BadgeCheck, Landmark
} from "lucide-react";
import logo from "../assets/images/company-logo.png";

function StepIndicator({ step }: { step: number }) {
  const items = [
    { label: "阅读注册协议", icon: FileText },
    { label: "填写基本信息", icon: User },
    { label: "提交成功", icon: CheckCircle }
  ];
  
  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '0',
      padding: '24px 0',
      backgroundColor: 'white',
      borderRadius: '16px',
      boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
    }}>
      {items.map((item, i) => {
        const Icon = item.icon;
        const isActive = i === step;
        const isPast = i < step;
        
        return (
          <React.Fragment key={item.label}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{
                width: '44px',
                height: '44px',
                borderRadius: '12px',
                backgroundColor: isActive ? '#003c8c' : isPast ? '#10b981' : '#f3f4f6',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.3s'
              }}>
                <Icon style={{ width: '22px', height: '22px', color: isActive || isPast ? 'white' : '#9ca3af' }} />
              </div>
              <span style={{
                fontSize: '15px',
                fontWeight: isActive ? 600 : 400,
                color: isActive ? '#003c8c' : isPast ? '#10b981' : '#9ca3af'
              }}>
                {item.label}
              </span>
            </div>
            {i < items.length - 1 && (
              <div style={{
                width: '60px',
                height: '3px',
                backgroundColor: isPast ? '#10b981' : '#e5e7eb',
                margin: '0 24px',
                borderRadius: '2px'
              }} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

export default function ExpertRegisterPage() {
  const [step, setStep] = useState(0);
  const navigate = useNavigate();
  
  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f8fafc' }}>
      {/* 顶部横幅 */}
      <div style={{
        background: 'linear-gradient(135deg, #003c8c 0%, #0050b8 50%, #0066cc 100%)',
        padding: '40px 0',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* 装饰元素 */}
        <div style={{
          position: 'absolute',
          top: '-50%',
          right: '-10%',
          width: '400px',
          height: '400px',
          borderRadius: '50%',
          background: 'rgba(255,255,255,0.05)',
        }} />
        <div style={{
          position: 'absolute',
          bottom: '-30%',
          left: '-5%',
          width: '250px',
          height: '250px',
          borderRadius: '50%',
          background: 'rgba(255,255,255,0.03)',
        }} />
        
        <div style={{ width: '80%', maxWidth: '900px', margin: '0 auto', position: 'relative', zIndex: 1, textAlign: 'center' }}>
          <img src={logo} alt="德润慧通" style={{ height: '50px', marginBottom: '16px', filter: 'brightness(0) invert(1)' }} />
          <h1 style={{ 
            fontSize: '28px', 
            fontWeight: 700, 
            color: 'white',
            marginBottom: '8px',
            textShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}>
            评审人员注册
          </h1>
        </div>
      </div>
      
      {/* 内容区域 */}
      <div style={{ padding: '40px 20px', minHeight: 'calc(100vh - 180px)', display: 'flex', flexDirection: 'column' }}>
        
        {/* 步骤指示器 */}
        <div style={{ maxWidth: '800px', margin: '0 auto', width: '100%' }}>
          <StepIndicator step={step} />
        </div>
        
        {/* 主卡片 */}
        <div style={{
          flex: 1,
          maxWidth: '900px',
          width: '100%',
          margin: '24px auto 0',
          backgroundColor: 'white',
          borderRadius: '20px',
          boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column'
        }}>
          {step === 0 && <Agreement onNext={() => setStep(1)} onCancel={() => { window.scrollTo(0, 0); navigate('/'); }} />}
          {step === 1 && <ExpertForm onSuccess={() => setStep(2)} onCancel={() => { window.scrollTo(0, 0); navigate('/'); }} />}
          {step === 2 && <Success />}
        </div>
      </div>
    </div>
  );
}

function Agreement({ onNext, onCancel }: { onNext: () => void; onCancel: () => void }) {
  const [left, setLeft] = useState(5);
  useEffect(() => {
    const t = setInterval(() => setLeft((s) => (s > 0 ? s - 1 : 0)), 1000);
    return () => clearInterval(t);
  }, []);
  
  const agreementContent = `根据《中华人民共和国合同法》、《中华人民共和国著作权法》、《全国人大常委会关于维护互联网安全的决定》、《中华人民共和国政府采购法》、《互联网信息服务管理办法》、《互联网电子公告服务管理规定》等有关法律、法规、规章，制订本条款。所有进入本平台的用户，在使用本平台提供的任何服务前，必须事先认真阅读并同意本服务协议。用户通过点击"我同意"按钮即表示用户与本招标采购平台达成协议，接受本招标采购平台服务条款全部内容。点击"不同意"按钮，视为放弃本招标采购平台提供的服务，并表示放弃注册信息库。

一、评审人员资料
评审人员同意：

(一)在申请使用本招标采购平台提供的服务时，向本招标采购平台提供真实、准确、完整的个人资料。

(二)如评审人员的个人资料有任何变更，应及时更新注册资料。如果提供的资料不完整或者包含有不正确或不准确的信息，由此相关的一切后果均应当由评审人员自行承担。

(三)评审人员资料包括您在注册、评标等过程中，以任何网络形式向本平台传送的任何资料，包括数据、文本、照片、图画、影像、词句或其他材料。您应对"您的资料"负全部责任。

二、服务条款的修改
本招标采购平台有权根据需要修改服务条款，本招标采购平台服务条款一旦发生修改或其它变动，将在条款相关页面予以提示。

三、隐私保护
本招标采购平台承诺不会公开、转让或透露评审人员的不可公开的注册资料及在本招标采购平台中保存的非公开内容，除非：

(一)有关法律、法规、规章的强制性规定；

(二)有关司法、行政机关的要求；

(三)保持或维护本招标采购平台的合法权益；

(四)事先获得评审人员的明确授权。

四、账号、密码和安全性
评审人员在本招标采购平台注册成功后，将获得一个帐号和密码。该帐号和密码由评审人员负责保管。每个评审人员都要对其帐户中的所有活动和事件负全部责任。

五、免责条款
因本招标采购平台无法预见客观环境的改变及任何技术上的问题或其他困难（包括但不限于：计算机病毒攻击、黑客入侵、网络中断等），由此导致的数据损失或其他服务中断等情形，评审人员对此应表示理解，并同意自行承担利用本招标采购平台进行操作的所有风险。

六、条款的法律解释
以上条款解释权归本平台所有。如发生本招标采购平台服务条款或所发布的其它条款与中华人民共和国法律、法规及规章相抵触时，则这些条款将完全按有关法律、法规及规章规定重新解释。若评审人员与本单位就服务条款发生任何争议，双方应尽量友好协商解决；协商不成时，任何一方应向成都仲裁委仲裁解决。

感谢您对本平台和服务的信任和使用！`;

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* 标题 */}
      <div style={{ padding: '32px 40px 24px', borderBottom: '1px solid #f3f4f6' }}>
        <h2 style={{ fontSize: '22px', fontWeight: 700, color: '#1f2937', marginBottom: '8px' }}>评审人员注册协议</h2>
        <p style={{ fontSize: '14px', color: '#6b7280' }}>请仔细阅读以下服务条款</p>
      </div>
      
      {/* 协议内容 */}
      <div style={{ flex: 1, overflow: 'auto', padding: '24px 40px' }}>
        <div style={{
          backgroundColor: '#f8fafc',
          borderRadius: '12px',
          padding: '24px',
          fontSize: '14px',
          lineHeight: 1.8,
          color: '#4b5563',
          whiteSpace: 'pre-wrap'
        }}>
          {agreementContent}
        </div>
      </div>
      
      {/* 按钮 */}
      <div style={{ padding: '24px 40px', borderTop: '1px solid #f3f4f6', display: 'flex', justifyContent: 'center', gap: '16px' }}>
        <button
          disabled={left > 0}
          onClick={onNext}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '14px 32px',
            backgroundColor: left > 0 ? '#e5e7eb' : '#003c8c',
            color: left > 0 ? '#9ca3af' : 'white',
            fontSize: '15px',
            fontWeight: 600,
            border: 'none',
            borderRadius: '12px',
            cursor: left > 0 ? 'not-allowed' : 'pointer',
            transition: 'all 0.2s'
          }}
        >
          {left > 0 ? `同意并下一步 (${left}s)` : '同意并下一步'}
          <ArrowRight style={{ width: '18px', height: '18px' }} />
        </button>
        <button
          onClick={onCancel}
          style={{
            padding: '14px 32px',
            backgroundColor: 'white',
            color: '#003c8c',
            fontSize: '15px',
            fontWeight: 600,
            border: '2px solid #003c8c',
            borderRadius: '12px',
            cursor: 'pointer'
          }}
        >
          取消
        </button>
      </div>
    </div>
  );
}

type FormState = {
  work_unit: string;
  expert_no: string;
  expert_name: string;
  gender: string;
  industry: string;
  title: string;
  position: string;
  phone: string;
  expert_type: string;
  remark: string;
  specialty: string;
  id_card: string;
  bank_name: string;
  bank_account: string;
  captchaId: string;
  captchaCode: string;
  smsCode: string;
};

// FormField 组件
const FormField = ({ id, label, icon: Icon, error, required = false, children }: { 
  id: string; 
  label: React.ReactNode; 
  icon: any; 
  error?: string; 
  required?: boolean;
  children: React.ReactNode 
}) => (
  <div>
    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
      <label style={{ fontSize: '14px', fontWeight: 500, color: '#374151' }}>
        {label} {required && <span style={{ color: '#ef4444' }}>*</span>}
      </label>
      {error && <span style={{ fontSize: '12px', color: '#ef4444' }}>{error}</span>}
    </div>
    {children}
  </div>
);

// 职务选项
const positionOptions = [
  '工程师',
  '高级工程师',
  '主任记者',
  '教授',
  '经济师',
  '高级经济师',
  '技术工人',
  '副教授',
  '会计师',
  '主任技师',
  '研究员',
  '副研究员',
];

function ExpertForm({ onSuccess, onCancel }: { onSuccess: () => void; onCancel: () => void }) {
  const [fs, setFs] = useState<FormState>({
    work_unit: "",
    expert_no: "",
    expert_name: "",
    gender: "",
    industry: "",
    title: "",
    position: "",
    phone: "",
    expert_type: "",
    remark: "",
    specialty: "",
    id_card: "",
    bank_name: "",
    bank_account: "",
    captchaId: "",
    captchaCode: "",
    smsCode: "",
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [captchaSvg, setCaptchaSvg] = useState<string>("");
  const [smsSending, setSmsSending] = useState(false);
  const [smsLeft, setSmsLeft] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [focusedField, setFocusedField] = useState<string | null>(null);
  const [showPhoneExistsModal, setShowPhoneExistsModal] = useState(false);

  useEffect(() => { refreshCaptcha(); }, []);

  function refreshCaptcha() {
    fetch('/api/captcha')
      .then(r => r.json())
      .then(d => {
        if (d && d.id && d.svg) {
          setFs(s => ({ ...s, captchaId: d.id, captchaCode: "" }));
          setCaptchaSvg(d.svg);
        } else {
          console.error('获取验证码失败:', d);
        }
      })
      .catch(err => {
        console.error('获取验证码错误:', err);
      });
  }

  // 身份证号验证
  function validateIdCard(idCard: string): boolean {
    const reg = /^[1-9]\d{5}(18|19|20)\d{2}((0[1-9])|(1[0-2]))(([0-2][1-9])|10|20|30|31)\d{3}[0-9Xx]$/;
    if (!reg.test(idCard)) return false;
    
    // 校验码验证
    const weights = [7, 9, 10, 5, 8, 4, 2, 1, 6, 3, 7, 9, 10, 5, 8, 4, 2];
    const checkCodes = ['1', '0', 'X', '9', '8', '7', '6', '5', '4', '3', '2'];
    let sum = 0;
    for (let i = 0; i < 17; i++) {
      sum += parseInt(idCard[i]) * weights[i];
    }
    const checkCode = checkCodes[sum % 11];
    return idCard[17].toUpperCase() === checkCode;
  }

  // 单个字段验证函数
  function validateField(fieldName: keyof FormState, value: string): string {
    switch (fieldName) {
      case 'expert_no':
        if (!value.trim()) return "专家证号不能为空";
        return "";
      case 'expert_name':
        if (!value.trim()) return "专家姓名不能为空";
        return "";
      case 'gender':
        if (!value) return "请选择性别";
        return "";
      case 'phone':
        if (!/^\d{11}$/.test(value)) return "电话号码需为11位数字";
        return "";
      case 'expert_type':
        if (!value) return "请选择专家类别";
        return "";
      case 'id_card':
        if (!value.trim()) return "身份证号不能为空";
        if (!validateIdCard(value)) return "身份证号格式不正确";
        return "";
      case 'bank_name':
        if (!value.trim()) return "开户银行不能为空";
        return "";
      case 'bank_account':
        if (!value.trim()) return "银行账号不能为空";
        if (!/^\d+$/.test(value)) return "银行账号必须为纯数字";
        return "";
      case 'smsCode':
        if (!value.trim()) return "请输入短信验证码";
        return "";
      default:
        return "";
    }
  }

  function validateAll(): boolean {
    const e: Record<string, string> = {};
    e.expert_no = validateField('expert_no', fs.expert_no);
    e.expert_name = validateField('expert_name', fs.expert_name);
    e.gender = validateField('gender', fs.gender);
    e.phone = validateField('phone', fs.phone);
    e.expert_type = validateField('expert_type', fs.expert_type);
    e.id_card = validateField('id_card', fs.id_card);
    e.bank_name = validateField('bank_name', fs.bank_name);
    e.bank_account = validateField('bank_account', fs.bank_account);
    e.smsCode = validateField('smsCode', fs.smsCode);
    setErrors(e);
    return Object.keys(e).every(k => !e[k]);
  }

  async function getSmsCode() {
    if (smsLeft > 0 || smsSending) return;
    const phoneOk = /^\d{11}$/.test(fs.phone);
    if (!phoneOk) {
      setErrors(prev => ({ ...prev, phone: "电话号码需为11位数字" }));
      return;
    }
    
    // 验证图形验证码
    if (!fs.captchaId || !fs.captchaCode) {
      setErrors(prev => ({ ...prev, captchaCode: "请输入图形验证码" }));
      return;
    }
    
    const captchaVerify = await fetch('/api/captcha/verify', {
      method: 'POST', 
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: fs.captchaId, code: fs.captchaCode })
    }).then(r => r.json()).catch(() => ({ ok: false }));
    
    if (!captchaVerify || !captchaVerify.ok) {
      setErrors(prev => ({ ...prev, captchaCode: "图形验证码错误" }));
      refreshCaptcha();
      return;
    }
    
    setErrors(prev => ({ ...prev, captchaCode: "" }));
    
    // 验证通过，发送短信
    setSmsSending(true);
    const r = await fetch('/api/sms/get', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ phone: fs.phone, user_name: fs.expert_no, check_type: 'expert' })
    }).then(r => r.json()).catch(() => ({ ok: false }));
    setSmsSending(false);
    if (r && r.ok) {
      setSmsLeft(60);
    } else if (r?.error === 'phone_exists') {
      // 手机号已被注册，显示弹窗提示
      setShowPhoneExistsModal(true);
    } else {
      setErrors(prev => ({ ...prev, smsCode: r?.error || "短信发送失败" }));
    }
  }

  useEffect(() => {
    if (smsLeft <= 0) return;
    const t = setInterval(() => setSmsLeft(s => (s > 0 ? s - 1 : 0)), 1000);
    return () => clearInterval(t);
  }, [smsLeft]);

  async function submit() {
    if (!validateAll()) return;
    
    setSubmitting(true);
    try {
      // 检查重复
      const dupCheck = await fetch('/api/expert/register/check', {
        method: 'POST', 
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          expert_no: fs.expert_no, 
          id_card: fs.id_card, 
          phone: fs.phone 
        })
      }).then(r => r.json()).catch(() => ({ ok: false }));

      if (dupCheck?.duplicates) {
        const d = dupCheck.duplicates;
        const newErrors: Record<string, string> = {};
        if (d.expert_no) newErrors.expert_no = "专家证号已存在";
        if (d.id_card) newErrors.id_card = "身份证号已存在";
        if (d.phone) newErrors.phone = "电话号码已存在";
        if (Object.keys(newErrors).length > 0) {
          setErrors(prev => ({ ...prev, ...newErrors }));
          setSubmitting(false);
          return;
        }
      }

      // 提交注册
      const resp = await fetch('/api/expert/register', {
        method: 'POST', 
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          work_unit: fs.work_unit || null,
          expert_no: fs.expert_no,
          expert_name: fs.expert_name,
          gender: fs.gender,
          industry: fs.industry || null,
          title: fs.title || null,
          position: fs.position || null,
          phone: fs.phone,
          expert_type: fs.expert_type,
          remark: fs.remark || null,
          specialty: fs.specialty || null,
          id_card: fs.id_card,
          bank_name: fs.bank_name,
          bank_account: fs.bank_account,
          sms_code: fs.smsCode,
        })
      }).then(r => r.json()).catch(() => ({ ok: false }));

      if (resp && resp.ok) {
        onSuccess();
      } else {
        const errorMsg = resp?.error || "注册失败，请稍后重试";
        setErrors(prev => ({ ...prev, smsCode: typeof errorMsg === 'string' ? errorMsg : "注册失败" }));
        refreshCaptcha();
      }
    } catch (err) {
      console.error('注册错误:', err);
      setErrors(prev => ({ ...prev, smsCode: "注册失败，请稍后重试" }));
      refreshCaptcha();
    } finally {
      setSubmitting(false);
    }
  }

  const inputStyle = (field: string, hasError: boolean) => ({
    width: '100%',
    height: '48px',
    padding: '0 16px 0 48px',
    border: `2px solid ${hasError ? '#ef4444' : focusedField === field ? '#003c8c' : '#e5e7eb'}`,
    borderRadius: '12px',
    fontSize: '15px',
    outline: 'none',
    transition: 'all 0.2s',
    backgroundColor: 'white'
  });

  const selectStyle = (field: string, hasError: boolean) => ({
    width: '100%',
    height: '48px',
    padding: '0 16px 0 48px',
    border: `2px solid ${hasError ? '#ef4444' : focusedField === field ? '#003c8c' : '#e5e7eb'}`,
    borderRadius: '12px',
    fontSize: '15px',
    outline: 'none',
    transition: 'all 0.2s',
    backgroundColor: 'white',
    cursor: 'pointer',
    appearance: 'none' as const,
    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%239ca3af' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6,9 12,15 18,9'%3E%3C/polyline%3E%3C/svg%3E")`,
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'right 16px center'
  });

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* 标题 */}
      <div style={{ padding: '32px 40px 24px', borderBottom: '1px solid #f3f4f6' }}>
        <h2 style={{ fontSize: '22px', fontWeight: 700, color: '#1f2937', marginBottom: '8px' }}>填写评审人员信息</h2>
        <p style={{ fontSize: '14px', color: '#6b7280' }}>请填写以下信息完成注册，带 <span style={{ color: '#ef4444' }}>*</span> 为必填项</p>
      </div>
      
      {/* 表单 */}
      <div style={{ flex: 1, overflow: 'auto', padding: '24px 40px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '20px' }}>
          
          {/* 工作单位 */}
          <FormField id="work_unit" label="工作单位" icon={Building2}>
            <div style={{ position: 'relative' }}>
              <Building2 style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)', width: '20px', height: '20px', color: focusedField === 'work_unit' ? '#003c8c' : '#9ca3af' }} />
              <input
                value={fs.work_unit}
                onChange={(e) => setFs(s => ({ ...s, work_unit: e.target.value }))}
                onFocus={() => setFocusedField('work_unit')}
                onBlur={() => setFocusedField(null)}
                style={inputStyle('work_unit', false)}
                placeholder="请输入工作单位（选填）"
              />
            </div>
          </FormField>

          {/* 专家证号 */}
          <FormField id="expert_no" label="专家证号" icon={BadgeCheck} error={errors.expert_no} required>
            <div style={{ position: 'relative' }}>
              <BadgeCheck style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)', width: '20px', height: '20px', color: focusedField === 'expert_no' ? '#003c8c' : '#9ca3af' }} />
              <input
                value={fs.expert_no}
                onChange={(e) => setFs(s => ({ ...s, expert_no: e.target.value }))}
                onFocus={() => setFocusedField('expert_no')}
                onBlur={() => setFocusedField(null)}
                style={inputStyle('expert_no', !!errors.expert_no)}
                placeholder="请输入专家证号"
              />
            </div>
          </FormField>

          {/* 专家姓名 */}
          <FormField id="expert_name" label="专家姓名" icon={User} error={errors.expert_name} required>
            <div style={{ position: 'relative' }}>
              <User style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)', width: '20px', height: '20px', color: focusedField === 'expert_name' ? '#003c8c' : '#9ca3af' }} />
              <input
                value={fs.expert_name}
                onChange={(e) => setFs(s => ({ ...s, expert_name: e.target.value }))}
                onFocus={() => setFocusedField('expert_name')}
                onBlur={() => setFocusedField(null)}
                style={inputStyle('expert_name', !!errors.expert_name)}
                placeholder="请输入专家姓名"
              />
            </div>
          </FormField>

          {/* 性别 */}
          <FormField id="gender" label="专家性别" icon={User} error={errors.gender} required>
            <div style={{ position: 'relative' }}>
              <User style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)', width: '20px', height: '20px', color: focusedField === 'gender' ? '#003c8c' : '#9ca3af', zIndex: 1 }} />
              <select
                value={fs.gender}
                onChange={(e) => setFs(s => ({ ...s, gender: e.target.value }))}
                onFocus={() => setFocusedField('gender')}
                onBlur={() => setFocusedField(null)}
                style={selectStyle('gender', !!errors.gender)}
              >
                <option value="">请选择性别</option>
                <option value="男">男</option>
                <option value="女">女</option>
              </select>
            </div>
          </FormField>

          {/* 专家行业 */}
          <FormField id="industry" label="专家行业" icon={Briefcase}>
            <div style={{ position: 'relative' }}>
              <Briefcase style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)', width: '20px', height: '20px', color: focusedField === 'industry' ? '#003c8c' : '#9ca3af' }} />
              <input
                value={fs.industry}
                onChange={(e) => setFs(s => ({ ...s, industry: e.target.value }))}
                onFocus={() => setFocusedField('industry')}
                onBlur={() => setFocusedField(null)}
                style={inputStyle('industry', false)}
                placeholder="请输入专家行业（选填）"
              />
            </div>
          </FormField>

          {/* 职称 */}
          <FormField id="title" label="职称" icon={Award}>
            <div style={{ position: 'relative' }}>
              <Award style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)', width: '20px', height: '20px', color: focusedField === 'title' ? '#003c8c' : '#9ca3af' }} />
              <input
                value={fs.title}
                onChange={(e) => setFs(s => ({ ...s, title: e.target.value }))}
                onFocus={() => setFocusedField('title')}
                onBlur={() => setFocusedField(null)}
                style={inputStyle('title', false)}
                placeholder="请输入职称（选填）"
              />
            </div>
          </FormField>

          {/* 职务 */}
          <FormField id="position" label="职务" icon={Briefcase}>
            <div style={{ position: 'relative' }}>
              <Briefcase style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)', width: '20px', height: '20px', color: focusedField === 'position' ? '#003c8c' : '#9ca3af', zIndex: 1 }} />
              <select
                value={fs.position}
                onChange={(e) => setFs(s => ({ ...s, position: e.target.value }))}
                onFocus={() => setFocusedField('position')}
                onBlur={() => setFocusedField(null)}
                style={selectStyle('position', false)}
              >
                <option value="">请选择职务（选填）</option>
                {positionOptions.map(p => (
                  <option key={p} value={p}>{p}</option>
                ))}
              </select>
            </div>
          </FormField>

          {/* 专家类别 */}
          <FormField id="expert_type" label="专家类别" icon={BookOpen} error={errors.expert_type} required>
            <div style={{ position: 'relative' }}>
              <BookOpen style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)', width: '20px', height: '20px', color: focusedField === 'expert_type' ? '#003c8c' : '#9ca3af', zIndex: 1 }} />
              <select
                value={fs.expert_type}
                onChange={(e) => setFs(s => ({ ...s, expert_type: e.target.value }))}
                onFocus={() => setFocusedField('expert_type')}
                onBlur={() => setFocusedField(null)}
                style={selectStyle('expert_type', !!errors.expert_type)}
              >
                <option value="">请选择专家类别</option>
                <option value="技术类">技术类</option>
                <option value="经济类">经济类</option>
              </select>
            </div>
          </FormField>

          {/* 专家专业 */}
          <FormField id="specialty" label="专家专业" icon={BookOpen}>
            <div style={{ position: 'relative' }}>
              <BookOpen style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)', width: '20px', height: '20px', color: focusedField === 'specialty' ? '#003c8c' : '#9ca3af' }} />
              <input
                value={fs.specialty}
                onChange={(e) => setFs(s => ({ ...s, specialty: e.target.value }))}
                onFocus={() => setFocusedField('specialty')}
                onBlur={() => setFocusedField(null)}
                style={inputStyle('specialty', false)}
                placeholder="请输入专家专业（选填）"
              />
            </div>
          </FormField>

          {/* 身份证号 */}
          <FormField id="id_card" label="身份证号" icon={CreditCard} error={errors.id_card} required>
            <div style={{ position: 'relative' }}>
              <CreditCard style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)', width: '20px', height: '20px', color: focusedField === 'id_card' ? '#003c8c' : '#9ca3af' }} />
              <input
                value={fs.id_card}
                onChange={(e) => setFs(s => ({ ...s, id_card: e.target.value }))}
                onFocus={() => setFocusedField('id_card')}
                onBlur={() => setFocusedField(null)}
                style={inputStyle('id_card', !!errors.id_card)}
                placeholder="请输入18位身份证号"
                maxLength={18}
              />
            </div>
          </FormField>

          {/* 开户银行 */}
          <FormField id="bank_name" label="开户银行" icon={Landmark} error={errors.bank_name} required>
            <div style={{ position: 'relative' }}>
              <Landmark style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)', width: '20px', height: '20px', color: focusedField === 'bank_name' ? '#003c8c' : '#9ca3af' }} />
              <input
                value={fs.bank_name}
                onChange={(e) => setFs(s => ({ ...s, bank_name: e.target.value }))}
                onFocus={() => setFocusedField('bank_name')}
                onBlur={() => setFocusedField(null)}
                style={inputStyle('bank_name', !!errors.bank_name)}
                placeholder="请输入开户银行"
              />
            </div>
          </FormField>

          {/* 银行账号 */}
          <FormField id="bank_account" label="银行账号" icon={CreditCard} error={errors.bank_account} required>
            <div style={{ position: 'relative' }}>
              <CreditCard style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)', width: '20px', height: '20px', color: focusedField === 'bank_account' ? '#003c8c' : '#9ca3af' }} />
              <input
                value={fs.bank_account}
                onChange={(e) => setFs(s => ({ ...s, bank_account: e.target.value }))}
                onFocus={() => setFocusedField('bank_account')}
                onBlur={() => setFocusedField(null)}
                style={inputStyle('bank_account', !!errors.bank_account)}
                placeholder="请输入银行账号（纯数字）"
              />
            </div>
          </FormField>

          {/* 电话号码 */}
          <FormField id="phone" label="电话号码" icon={Phone} error={errors.phone} required>
            <div style={{ position: 'relative' }}>
              <Phone style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)', width: '20px', height: '20px', color: focusedField === 'phone' ? '#003c8c' : '#9ca3af' }} />
              <input
                type="tel"
                value={fs.phone}
                onChange={(e) => setFs(s => ({ ...s, phone: e.target.value }))}
                onFocus={() => setFocusedField('phone')}
                onBlur={() => setFocusedField(null)}
                style={inputStyle('phone', !!errors.phone)}
                placeholder="请输入11位手机号"
              />
            </div>
          </FormField>

          {/* 图形验证码 */}
          <FormField id="captchaCode" label="图形验证码" icon={Shield} error={errors.captchaCode} required>
            <div style={{ display: 'flex', gap: '12px' }}>
              <div style={{ position: 'relative', flex: 1 }}>
                <Shield style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)', width: '20px', height: '20px', color: focusedField === 'captchaCode' ? '#003c8c' : '#9ca3af' }} />
                <input
                  value={fs.captchaCode}
                  onChange={(e) => setFs(s => ({ ...s, captchaCode: e.target.value }))}
                  onFocus={() => setFocusedField('captchaCode')}
                  onBlur={() => setFocusedField(null)}
                  style={inputStyle('captchaCode', !!errors.captchaCode)}
                  placeholder="请输入验证码"
                />
              </div>
              <div
                onClick={refreshCaptcha}
                style={{
                  width: '120px',
                  height: '48px',
                  backgroundColor: '#f3f4f6',
                  borderRadius: '12px',
                  overflow: 'hidden',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: '1px solid #e5e7eb',
                  flexShrink: 0
                }}
              >
                {captchaSvg ? <div dangerouslySetInnerHTML={{ __html: captchaSvg }} /> : <span style={{ fontSize: '12px', color: '#9ca3af' }}>点击刷新</span>}
              </div>
            </div>
          </FormField>

          {/* 短信验证码 - 占整行 */}
          <div style={{ gridColumn: 'span 2' }}>
            <FormField 
              id="smsCode" 
              label={
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span>短信验证码</span>
                  {smsLeft > 0 && <span style={{ color: '#ef4444', fontSize: '13px', fontWeight: 500 }}>验证码有效期60秒，请尽快填写！</span>}
                </div>
              }
              icon={Shield} 
              error={errors.smsCode}
              required
            >
              <div style={{ display: 'flex', gap: '12px' }}>
                <div style={{ position: 'relative', flex: 1 }}>
                  <Shield style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)', width: '20px', height: '20px', color: focusedField === 'smsCode' ? '#003c8c' : '#9ca3af' }} />
                  <input
                    value={fs.smsCode}
                    onChange={(e) => setFs(s => ({ ...s, smsCode: e.target.value }))}
                    onFocus={() => setFocusedField('smsCode')}
                    onBlur={() => setFocusedField(null)}
                    style={inputStyle('smsCode', !!errors.smsCode)}
                    placeholder="请输入短信验证码"
                  />
                </div>
                <button
                  onClick={getSmsCode}
                  disabled={!/^\d{11}$/.test(fs.phone) || smsLeft > 0 || smsSending || !fs.captchaCode.trim()}
                  style={{
                    padding: '0 24px',
                    height: '48px',
                    backgroundColor: (!/^\d{11}$/.test(fs.phone) || smsLeft > 0 || smsSending || !fs.captchaCode) ? '#e5e7eb' : '#003c8c',
                    color: (!/^\d{11}$/.test(fs.phone) || smsLeft > 0 || smsSending || !fs.captchaCode) ? '#9ca3af' : 'white',
                    border: 'none',
                    borderRadius: '12px',
                    fontSize: '14px',
                    fontWeight: 500,
                    cursor: (!/^\d{11}$/.test(fs.phone) || smsLeft > 0 || smsSending || !fs.captchaCode) ? 'not-allowed' : 'pointer',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {smsLeft > 0 ? `重新获取(${smsLeft}s)` : smsSending ? '发送中...' : '获取验证码'}
                </button>
              </div>
            </FormField>
          </div>

          {/* 备注 - 占整行 */}
          <div style={{ gridColumn: 'span 2' }}>
            <FormField id="remark" label="备注" icon={FileText}>
              <div style={{ position: 'relative' }}>
                <FileText style={{ position: 'absolute', left: '16px', top: '16px', width: '20px', height: '20px', color: focusedField === 'remark' ? '#003c8c' : '#9ca3af' }} />
                <textarea
                  value={fs.remark}
                  onChange={(e) => setFs(s => ({ ...s, remark: e.target.value }))}
                  onFocus={() => setFocusedField('remark')}
                  onBlur={() => setFocusedField(null)}
                  style={{
                    width: '100%',
                    minHeight: '80px',
                    padding: '12px 16px 12px 48px',
                    border: `2px solid ${focusedField === 'remark' ? '#003c8c' : '#e5e7eb'}`,
                    borderRadius: '12px',
                    fontSize: '15px',
                    outline: 'none',
                    transition: 'all 0.2s',
                    backgroundColor: 'white',
                    resize: 'vertical'
                  }}
                  placeholder="请输入备注信息（选填）"
                />
              </div>
            </FormField>
          </div>
        </div>
      </div>
      
      {/* 按钮 */}
      <div style={{ padding: '24px 40px', borderTop: '1px solid #f3f4f6', display: 'flex', justifyContent: 'center', gap: '16px' }}>
        <button
          onClick={submit}
          disabled={submitting}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '14px 48px',
            backgroundColor: submitting ? '#9ca3af' : '#003c8c',
            color: 'white',
            fontSize: '15px',
            fontWeight: 600,
            border: 'none',
            borderRadius: '12px',
            cursor: submitting ? 'not-allowed' : 'pointer',
            boxShadow: submitting ? 'none' : '0 4px 15px rgba(0,60,140,0.3)'
          }}
        >
          {submitting ? '提交中...' : '提交注册'}
        </button>
        <button
          onClick={onCancel}
          style={{
            padding: '14px 32px',
            backgroundColor: 'white',
            color: '#003c8c',
            fontSize: '15px',
            fontWeight: 600,
            border: '2px solid #003c8c',
            borderRadius: '12px',
            cursor: 'pointer'
          }}
        >
          取消
        </button>
      </div>

      {/* 手机号已存在弹窗 */}
      {showPhoneExistsModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '16px',
            width: '450px',
            maxWidth: '90%',
            padding: '32px',
            boxShadow: '0 10px 25px rgba(0,0,0,0.1)'
          }}>
            <div style={{ textAlign: 'center', marginBottom: '24px' }}>
              <div style={{ 
                width: '60px', 
                height: '60px', 
                backgroundColor: '#fef3c7', 
                borderRadius: '50%', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                margin: '0 auto 16px'
              }}>
                <Phone style={{ width: '30px', height: '30px', color: '#f59e0b' }} />
              </div>
              <h3 style={{ fontSize: '20px', fontWeight: 700, color: '#1f2937', marginBottom: '16px' }}>提示</h3>
              <p style={{ fontSize: '15px', color: '#4b5563', lineHeight: 1.6 }}>
                该手机号已被注册使用，有任何疑问请联系管理员，感谢您的支持，谢谢！
              </p>
            </div>
            <button
              onClick={() => setShowPhoneExistsModal(false)}
              style={{
                width: '100%',
                padding: '12px',
                backgroundColor: '#003c8c',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '15px',
                fontWeight: 600,
                cursor: 'pointer'
              }}
            >
              我知道了
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function Success() {
  const navigate = useNavigate();
  
  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '60px 40px' }}>
      <div style={{
        width: '120px',
        height: '120px',
        borderRadius: '50%',
        backgroundColor: '#ecfdf5',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: '32px'
      }}>
        <CheckCircle style={{ width: '60px', height: '60px', color: '#10b981' }} />
      </div>
      
      <h2 style={{ fontSize: '28px', fontWeight: 700, color: '#1f2937', marginBottom: '12px' }}>提交成功！</h2>
      <p style={{ fontSize: '16px', color: '#6b7280', marginBottom: '48px', textAlign: 'center', maxWidth: '400px' }}>
        您的评审人员信息已提交成功，请等待后台审核。<br />审核结果会通过短信进行通知。
      </p>
      
      <div style={{ display: 'flex', gap: '16px' }}>
        <button
          onClick={() => { window.scrollTo(0, 0); navigate('/'); }}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '14px 48px',
            backgroundColor: '#003c8c',
            color: 'white',
            fontSize: '15px',
            fontWeight: 600,
            border: 'none',
            borderRadius: '12px',
            cursor: 'pointer',
            boxShadow: '0 4px 15px rgba(0,60,140,0.3)'
          }}
        >
          返回首页
          <ArrowRight style={{ width: '18px', height: '18px' }} />
        </button>
      </div>
    </div>
  );
}
